<template>
  <div class="my-ticket-page">
    <Card title="未完成订单（待支付）" :bordered="false" class="block-card">
      <Spin :spinning="loading">
        <template v-if="unpaidOrders.length">
          <div v-for="item in unpaidOrders" :key="item.orderSn" class="unpaid-card">
            <div class="unpaid-head">
              <span class="sn">订单号 {{ item.orderSn }}</span>
              <Tag color="orange">待支付</Tag>
            </div>
            <div class="route-line">
              {{ item.departure }} → {{ item.arrival }} · {{ item.trainNumber }} 次 ·
              {{ item.ridingDate }} {{ item.departureTime }} 开
            </div>
            <div class="countdown">
              支付剩余时间：
              <span class="time">{{ item.countdownText }}</span>
            </div>
            <Space>
              <Button type="primary" @click="goPay(item.orderSn)">立即支付</Button>
              <Button @click="goOrderDetail(item.orderSn)">订单详情</Button>
              <Button @click="cancel(item.orderSn)">取消订单</Button>
            </Space>
          </div>
        </template>
        <Empty v-else description="暂无待支付订单" />
      </Spin>
    </Card>
    <Card title="说明" :bordered="false" class="block-card">
      <p class="tip-p">
        未完成订单须在支付时限内完成网上支付，超时订单将自动取消。您也可在「车票订单」中查看全部记录。
      </p>
      <Button type="link" @click="router.push('/ticketList')">前往车票订单</Button>
    </Card>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { Card, Spin, Empty, Space, Button, Tag, message } from 'ant-design-vue'
import dayjs from 'dayjs'
import Cookie from 'js-cookie'
import { fetchTicketList, fetchOrderCancel } from '@/service'

const router = useRouter()
const userId = Cookie.get('userId')
const loading = ref(false)
const rawRecords = ref([])
let timer = null

const PAY_MINUTES = 30

/** 从订单列表未完成态聚合出订单维度 */
const unpaidOrders = computed(() => {
  const map = new Map()
  for (const row of rawRecords.value) {
    if (!row.orderSn) continue
    if (!map.has(row.orderSn)) {
      const deadline = row.orderTime
        ? dayjs(row.orderTime).add(PAY_MINUTES, 'minute')
        : dayjs().add(PAY_MINUTES, 'minute')
      map.set(row.orderSn, {
        orderSn: row.orderSn,
        trainNumber: row.trainNumber,
        departure: row.departure,
        arrival: row.arrival,
        ridingDate: row.ridingDate,
        departureTime: row.departureTime,
        orderTime: row.orderTime,
        deadlineMs: deadline.valueOf()
      })
    }
  }
  const now = Date.now()
  return [...map.values()].map((o) => {
    const left = Math.max(0, o.deadlineMs - now)
    const m = Math.floor(left / 60000)
    const s = Math.floor((left % 60000) / 1000)
    return {
      ...o,
      countdownText: left <= 0 ? '已超时，请尽快在订单列表处理' : `${m} 分 ${s} 秒`
    }
  })
})

const load = () => {
  loading.value = true
  fetchTicketList({ userId, current: 1, size: 50, statusType: 0 })
    .then((res) => {
      if (res.success && res.data?.records) {
        const flat = []
        res.data.records.forEach((info) => {
          info.passengerDetails?.forEach((item) => {
            flat.push({ ...info, ...item })
          })
        })
        rawRecords.value = flat
      } else {
        rawRecords.value = []
      }
    })
    .finally(() => {
      loading.value = false
    })
}

const goPay = (orderSn) => {
  router.push(`/order?sn=${orderSn}`)
}

const goOrderDetail = (orderSn) => {
  router.push({ path: '/orderDetail', query: { orderSn } })
}

const cancel = (orderSn) => {
  fetchOrderCancel({ orderSn }).then((res) => {
    if (res.success) {
      message.success('订单已取消')
      load()
    } else {
      message.error(res.message || '取消失败')
    }
  })
}

onMounted(() => {
  load()
  timer = setInterval(() => {
    rawRecords.value = [...rawRecords.value]
  }, 1000)
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<style lang="scss" scoped>
.my-ticket-page {
  max-width: 900px;
  margin: 0 auto;
}
.block-card {
  margin-bottom: 16px;
  border-radius: 12px;
}
.unpaid-card {
  padding: 14px 16px;
  border: 1px solid #fde68a;
  border-radius: 12px;
  background: linear-gradient(180deg, #fffbeb 0%, #ffffff 100%);
  margin-bottom: 12px;
}
.unpaid-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  .sn {
    font-weight: 600;
    color: #0f172a;
  }
}
.route-line {
  color: #475569;
  margin-bottom: 10px;
  font-size: 14px;
}
.countdown {
  margin-bottom: 12px;
  font-size: 14px;
  .time {
    color: #ea580c;
    font-weight: 700;
  }
}
.tip-p {
  color: #64748b;
  line-height: 1.7;
  margin-bottom: 8px;
}
</style>
